import { ItemClasses } from '../../../common/constants'
import { ActualItem, EstimatedItem, WorkareaItemBase, isActualItem, isEstimatedItem } from '../../../domain/workareaItem'
import { sum } from '../../../helpers/array'
import { roundToPrecision } from '../../../helpers/math'
import { ChangeSet, ItemWorkareaData, getUpdatedItem } from '../itemModel'

export type TimeAnalysisRow = {
  workareaName: string
  actualHours: number
  estimatedHours: number
  percentage: number | undefined
  normalizedPercentage: number
  key: number | string
  disabled: boolean,
  isSummaryRow?: boolean,
  children?: TimeAnalysisRow[]
}

const hasValue = <T>(value: T | undefined | null): value is T => {
  return value !== undefined && value !== null
}

const filterLaborByWorkareaId = (enabledWorkareaIds: number[], item: WorkareaItemBase): boolean => {
  return item.class === ItemClasses.Labor && enabledWorkareaIds.includes(item.workareaId)
}

const filterByVisit = (visitFilters: VisitFilter[], item: ActualItem): boolean => {
  if (!hasValue(item.visitId)) {
    return true
  }

  const compoundId = `${item.visitId}__${item.workareaId}`
  const matchingFilter = visitFilters.find(x => x.compoundId === compoundId)
  if (!matchingFilter) {
    return true
  }

  return matchingFilter.include
}

const getPercentage = (estimatedHours: number, actualHours: number): number | undefined => {
  if (estimatedHours === 0) {
    return undefined
  }

  return roundToPrecision(actualHours / estimatedHours, 2)
}
const getNormalizedPercentage = (estimatedHours: number, actualHours: number): number => {
  if (estimatedHours === 0) {
    return actualHours > 0 ? 1 : 0
  }

  if (actualHours > estimatedHours) {
    return roundToPrecision(estimatedHours / actualHours, 2)
  }

  return roundToPrecision(actualHours / estimatedHours, 2)
}

export type VisitFilter = {
  compoundId: string
  include: boolean
}

export const createTimeAnalysisRows = (
  estimatedItems: EstimatedItem[],
  actualItems: ActualItem[],
  changeSet: ChangeSet,
  workareas: ItemWorkareaData[],
  enabledWorkareaIds: number[],
  visitFilters: VisitFilter[],
  allowRecurse: boolean = true
): TimeAnalysisRow => {
  enabledWorkareaIds = enabledWorkareaIds.filter(x => {
    const workarea = workareas.find(w => w.id === x)
    return workarea?.isApproved && !workarea.isOnHold
  })
  const estimatedHoursList = estimatedItems
    .concat(changeSet.addedItems.filter(x => isEstimatedItem(x)) as EstimatedItem[])
    .filter(x => filterLaborByWorkareaId(enabledWorkareaIds, x))
    .map(x => getUpdatedItem(x, changeSet))
    .filter(x => !x.isDeleted)
    .map(x => {
      const workarea = workareas.find(w => w.id === x.item.workareaId)
      return {
        workareaId: x.item.workareaId,
        quantity: x.item.quantity * (workarea?.estimatedOccurrences ?? 1)
      }
    })

  const actualHoursList = actualItems
    .concat(changeSet.addedItems.filter(x => isActualItem(x)) as ActualItem[])
    .filter(x => filterLaborByWorkareaId(enabledWorkareaIds, x))
    .filter(x => filterByVisit(visitFilters, x))
    .map(x => getUpdatedItem(x, changeSet))
    .filter(x => !x.isDeleted)
    .map(x => ({
      workareaId: x.item.workareaId,
      quantity: x.item.quantity
    }))

  const totalEstimatedHours = sum(estimatedHoursList, x => x.quantity)
  const totalActualHours = sum(actualHoursList, x => x.quantity)
  const summaryRow: TimeAnalysisRow = {
    workareaName: allowRecurse ? 'Entire Job' : workareas[0].name,
    estimatedHours: totalEstimatedHours,
    actualHours: totalActualHours,
    key: allowRecurse ? 'entire-project' : workareas[0].id,
    percentage: getPercentage(totalEstimatedHours, totalActualHours),
    normalizedPercentage: getNormalizedPercentage(totalEstimatedHours, totalActualHours),
    disabled: false,
    isSummaryRow: true,
    children: workareas.filter(x => !x.isOnHold && (!allowRecurse || !x.masterWorkareaId)).map(w => {
      if (w.master && allowRecurse) {
        const childWorkareas = workareas.filter(x => x.masterWorkareaId === w.id)
        if (childWorkareas.length > 0) {
          const enabledChildWorkareaIds = enabledWorkareaIds.filter(x => x === w.id || childWorkareas.some(y => y.id === x))
          return createTimeAnalysisRows(estimatedItems, actualItems, changeSet, [w, ...childWorkareas], enabledChildWorkareaIds, visitFilters, false)
        }
      }

      const estimatedHours = sum(estimatedHoursList.filter(x => x.workareaId === w.id), x => x.quantity)
      const actualHours = sum(actualHoursList.filter(x => x.workareaId === w.id), x => x.quantity)
      const scheduleDate = w.scheduleDateUtc
        ? new Date(Date.parse(w.scheduleDateUtc)).toLocaleDateString('en-US')
        : undefined

      return {
        workareaName: `${w.name}${scheduleDate ? ' (' + scheduleDate + ')' : ''}`,
        estimatedHours,
        actualHours,
        key: w.id,
        percentage: getPercentage(estimatedHours, actualHours),
        normalizedPercentage: getNormalizedPercentage(estimatedHours, actualHours),
        disabled: !enabledWorkareaIds.includes(w.id)
      }
    })
  }

  return summaryRow
}
